/**
 * Game controller mapping, kept pure so it can be tested without a browser. Each frame the
 * glue (core/Gamepads.ts) hands over a snapshot of the pad and what the game is doing; this
 * turns it into the keys the game already understands, plus analog movement, look and menu
 * navigation.
 */

/** Button indices in the browser's "standard" gamepad mapping (Xbox names; PlayStation alike). */
export const PAD = {
  A: 0, // Cross
  B: 1, // Circle
  X: 2, // Square
  Y: 3, // Triangle
  LB: 4,
  RB: 5,
  LT: 6,
  RT: 7,
  BACK: 8, // Share / Select
  START: 9, // Options
  L3: 10,
  R3: 11,
  UP: 12,
  DOWN: 13,
  LEFT: 14,
  RIGHT: 15,
} as const;

/** What the pad is driving right now. */
export type PadContext = 'play' | 'drive' | 'stage' | 'menu' | 'none';

export type Dir = 'up' | 'down' | 'left' | 'right';

export interface PadSnapshot {
  /** Button values 0..1 (triggers are analog). */
  buttons: readonly number[];
  /** Stick axes: [leftX, leftY, rightX, rightY], -1..1, y down. */
  axes: readonly number[];
}

export interface PadFrame {
  /** Keys that should be held down right now (KeyboardEvent codes). */
  hold: Set<string>;
  /** Keys to tap (press and release) this frame. */
  tap: string[];
  /** Movement: x right, y forward, -1..1. */
  move: { x: number; y: number };
  /** Look this frame, in mouse-pixel equivalents. */
  look: { x: number; y: number };
  /** Camera zoom steps (like wheel notches; positive zooms out). */
  zoom: number;
  /** Menus: move the focus. */
  nav: Dir | null;
  /** Menus: press the focused button. */
  accept: boolean;
  /** Menus: scroll, in pixels this frame. */
  scroll: number;
  /** Anything on the pad was touched this frame. */
  active: boolean;
}

/** The controller layout, for the help screen. Keep in step with the tables below. */
export const PAD_HELP: ReadonlyArray<[string, string]> = [
  ['Left stick', 'Walk · click to toggle SPORT MODE'],
  ['Right stick', 'Look around · click for first person'],
  ['LT / RT', 'Hold for SPORT MODE (sprint) · driving: brake / gas'],
  ['A', 'Interact / talk / use · get out of the car'],
  ['B', 'Jump / stunt timing · hold for the handbrake · hop off the Rascal'],
  ['X', 'Crack a cold one · honk while driving'],
  ['Y', 'Eat'],
  ['LB / RB', 'Chug a Mtn Dewd / scratch a ticket'],
  ['D-pad', '↑ flashlight · ↓ fireworks · ←/→ zoom'],
  ['Start / Back', 'Phone / music on-off'],
  ['Menus', 'D-pad moves · A picks · B backs out · right stick scrolls'],
  ['Mini-games', 'A (or RT) is the action · sticks aim · LT scope · LB hold breath · B bails'],
];

const STICK_DEAD = 0.2;
const TRIGGER_ON = 0.35;
const NAV_ON = 0.55;
const NAV_DELAY = 0.38;
const NAV_REPEAT = 0.11;
/** Full stick deflection, in mouse pixels per second. */
const LOOK_RATE = { x: 560, y: 400 };
const STAGE_LOOK_RATE = 520;
const SCROLL_RATE = 900;

/** Radial dead zone with the live range rescaled back to 0..1. */
export function deadzone(x: number, y: number, dead = STICK_DEAD): { x: number; y: number } {
  const len = Math.hypot(x, y);
  if (len < dead) return { x: 0, y: 0 };
  const k = Math.min(1, (len - dead) / (1 - dead)) / len;
  return { x: x * k, y: y * k };
}

/** Gentle at the center, full speed at the edge: easier to aim. */
const curve = (v: number): number => Math.sign(v) * v * v;

/** One-press hotkeys on foot. */
const PLAY_TAPS: ReadonlyArray<[number, string]> = [
  [PAD.A, 'KeyE'],
  [PAD.X, 'KeyQ'],
  [PAD.Y, 'KeyF'],
  [PAD.LB, 'KeyR'],
  [PAD.RB, 'KeyT'],
  [PAD.UP, 'KeyL'],
  [PAD.DOWN, 'KeyG'],
  [PAD.R3, 'KeyV'],
  [PAD.START, 'KeyP'],
  [PAD.BACK, 'KeyM'],
];

const DRIVE_TAPS: ReadonlyArray<[number, string]> = [
  [PAD.A, 'KeyE'],
  [PAD.X, 'KeyH'],
  [PAD.UP, 'KeyL'],
  [PAD.R3, 'KeyV'],
  [PAD.START, 'KeyP'],
  [PAD.BACK, 'KeyM'],
];

export class PadMapper {
  private prev: number[] = [];
  /** Clicking the left stick latches Sport Mode, like the touch sprint button. */
  private sprintLatch = false;
  private navDir: Dir | null = null;
  private navTimer = 0;

  update(pad: PadSnapshot, ctx: PadContext, dt: number): PadFrame {
    const btn = (i: number): number => pad.buttons[i] ?? 0;
    const down = (i: number): boolean => btn(i) > (i === PAD.LT || i === PAD.RT ? TRIGGER_ON : 0.5);
    const wasDown = (i: number): boolean =>
      (this.prev[i] ?? 0) > (i === PAD.LT || i === PAD.RT ? TRIGGER_ON : 0.5);
    const pressed = (i: number): boolean => down(i) && !wasDown(i);

    const left = deadzone(pad.axes[0] ?? 0, pad.axes[1] ?? 0);
    const right = deadzone(pad.axes[2] ?? 0, pad.axes[3] ?? 0);
    const out: PadFrame = {
      hold: new Set(),
      tap: [],
      move: { x: 0, y: 0 },
      look: { x: 0, y: 0 },
      zoom: 0,
      nav: null,
      accept: false,
      scroll: 0,
      active:
        pad.buttons.some((v, i) => v > 0.5 !== (this.prev[i] ?? 0) > 0.5) ||
        left.x !== 0 ||
        left.y !== 0 ||
        right.x !== 0 ||
        right.y !== 0,
    };

    if (ctx === 'play' || ctx === 'drive') {
      out.move = { x: left.x, y: -left.y };
      out.look = { x: curve(right.x) * LOOK_RATE.x * dt, y: curve(right.y) * LOOK_RATE.y * dt };
      if (ctx === 'play') {
        if (pressed(PAD.L3)) this.sprintLatch = !this.sprintLatch;
        if (this.sprintLatch || down(PAD.LT) || down(PAD.RT)) out.hold.add('ShiftLeft');
        if (down(PAD.B)) out.hold.add('Space');
        for (const [b, code] of PLAY_TAPS) if (pressed(b)) out.tap.push(code);
        if (pressed(PAD.LEFT)) out.zoom -= 1;
        if (pressed(PAD.RIGHT)) out.zoom += 1;
      } else {
        // Triggers are the pedals; the stick still works for both.
        const pedal = btn(PAD.RT) - btn(PAD.LT);
        out.move.y = Math.max(-1, Math.min(1, out.move.y + pedal));
        if (down(PAD.B)) out.hold.add('Space');
        for (const [b, code] of DRIVE_TAPS) if (pressed(b)) out.tap.push(code);
      }
    } else if (ctx === 'stage') {
      if (down(PAD.A) || down(PAD.RT)) out.hold.add('Space');
      if (down(PAD.X)) out.hold.add('KeyE');
      if (down(PAD.LT)) out.hold.add('KeyZ');
      if (down(PAD.LB) || down(PAD.RB)) out.hold.add('ShiftLeft');
      const dirs = this.dirs(pad, left, 0.5);
      if (dirs.up) out.hold.add('ArrowUp');
      if (dirs.down) out.hold.add('ArrowDown');
      if (dirs.left) out.hold.add('ArrowLeft');
      if (dirs.right) out.hold.add('ArrowRight');
      out.look = { x: curve(right.x) * STAGE_LOOK_RATE * dt, y: curve(right.y) * STAGE_LOOK_RATE * dt };
      if (pressed(PAD.B)) out.tap.push('Escape');
    } else if (ctx === 'menu') {
      out.nav = this.navigate(pad, left, dt);
      out.accept = pressed(PAD.A);
      if (pressed(PAD.B) || pressed(PAD.START)) out.tap.push('Escape');
      out.scroll = right.y * SCROLL_RATE * dt;
    }
    if (ctx !== 'menu') this.navDir = null;

    this.prev = pad.buttons.slice();
    return out;
  }

  private dirs(pad: PadSnapshot, stick: { x: number; y: number }, on: number): Record<Dir, boolean> {
    const b = (i: number): boolean => (pad.buttons[i] ?? 0) > 0.5;
    return {
      up: b(PAD.UP) || stick.y < -on,
      down: b(PAD.DOWN) || stick.y > on,
      left: b(PAD.LEFT) || stick.x < -on,
      right: b(PAD.RIGHT) || stick.x > on,
    };
  }

  /** One step per press, then auto-repeat while held, like a keyboard. */
  private navigate(pad: PadSnapshot, stick: { x: number; y: number }, dt: number): Dir | null {
    const d = this.dirs(pad, stick, NAV_ON);
    const dir: Dir | null = d.up ? 'up' : d.down ? 'down' : d.left ? 'left' : d.right ? 'right' : null;
    if (dir !== this.navDir) {
      this.navDir = dir;
      this.navTimer = NAV_DELAY;
      return dir;
    }
    if (!dir) return null;
    this.navTimer -= dt;
    if (this.navTimer > 0) return null;
    this.navTimer = NAV_REPEAT;
    return dir;
  }
}
